import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import { ChevronDown } from 'lucide-react';

// Контекст для управления активным dropdown
interface DropdownContextType {
  activeDropdown: string | null;
  setActiveDropdown: (id: string | null) => void;
}

const DropdownContext = createContext<DropdownContextType | null>(null);

export const useDropdownContext = () => {
  const context = useContext(DropdownContext);
  if (!context) {
    throw new Error('useDropdownContext must be used within DropdownProvider');
  }
  return context;
};

interface DropdownProviderProps {
  children: React.ReactNode;
}

export const DropdownProvider: React.FC<DropdownProviderProps> = ({ children }) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  return (
    <DropdownContext.Provider value={{ activeDropdown, setActiveDropdown }}>
      {children}
    </DropdownContext.Provider>
  );
};

interface DropdownItem {
  id: string;
  label: string;
  icon?: React.ComponentType<any>;
  onClick?: () => void;
}

interface DropdownProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  className?: string;
  align?: 'left' | 'right';
  id: string; // Уникальный ID для каждого dropdown
}

export const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  className = '',
  align = 'left',
  id
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const { activeDropdown, setActiveDropdown } = useDropdownContext();

  // Определяем тип устройства
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Обработка hover для десктопа
  const handleMouseEnter = () => {
    if (!isMobile) {
      window.dispatchEvent(new CustomEvent('closeCommandPanel'));
      // Очищаем таймер
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Мгновенно открываем новый dropdown
      setActiveDropdown(id);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      // Простая задержка 200мс при уходе мыши
      timeoutRef.current = setTimeout(() => {
        setActiveDropdown(null);
      }, 200);
    }
  };

  // Обработка клика на кнопку
  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent('closeCommandPanel'));
    if (isMobile) {
      setIsOpen(!isOpen);
    } else {
      // На десктопе просто открываем dropdown при клике
      setActiveDropdown(id);
    }
    // Снимаем фокус с кнопки после клика
    e.currentTarget.blur();
  };

  // Закрытие при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        if (isMobile) {
          setIsOpen(false);
        } else {
          setActiveDropdown(null);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, setActiveDropdown]);

  // Закрытие при скролле на мобильных
  useEffect(() => {
    const handleScroll = () => {
      if (isMobile) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Закрытие при изменении размера окна
  useEffect(() => {
    const handleResize = () => {
      if (!isMobile) {
        setActiveDropdown(null);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, setActiveDropdown]);

  // Глобальный обработчик для отслеживания перехода между dropdown
  useEffect(() => {
    if (isMobile) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      const elementUnderMouse = document.elementFromPoint(e.clientX, e.clientY);
      const isOverDropdown = elementUnderMouse?.closest('.relative');
      
      if (isOverDropdown && timeoutRef.current) {
        // Если мышь над dropdown областью, отменяем таймер
        clearTimeout(timeoutRef.current);
      }
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    return () => document.removeEventListener('mousemove', handleGlobalMouseMove);
  }, [isMobile]);

  const isActive = isMobile ? isOpen : activeDropdown === id;
  const shouldShow = isActive;

  return (
    <div 
      ref={dropdownRef}
      className={`relative inline-block ${className} ${!isMobile && isActive ? 'hover-state' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={handleButtonClick}
        className={`flex items-center justify-center w-10 h-10 text-textSecondary transition-colors duration-200 rounded-lg button-outline focus:outline-none focus:ring-0 active:transform-none ${isMobile ? 'hover:text-text hover:bg-hover' : ''} ${isActive ? 'button-glow' : ''}`}
        style={{ cursor: isMobile ? 'pointer' : 'default' }}
      >
        {trigger}
      </button>

      {shouldShow && (
        <div 
          className={`absolute top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-lg py-1 ${align === 'right' ? 'right-0' : 'left-0'} min-w-48 md:min-w-56 lg:min-w-64`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {items.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.id}
                onMouseDown={(e) => {
                  item.onClick?.();
                  // Снимаем фокус с кнопки пункта меню после клика
                  e.currentTarget.blur();
                  setTimeout(() => {
                    setIsOpen(false);
                    setActiveDropdown(null);
                  }, 0);
                }}
                className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-textSecondary hover:text-text hover:bg-hover transition-colors duration-200 whitespace-nowrap"
              >
                {IconComponent && <IconComponent className="w-4 h-4 flex-shrink-0" />}
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}; 